/* Sting-Less product page (sections/sl-pdp-main.liquid): gallery, purchase options, quantity, add to cart.
   From the Claude Design handoff 2026-10-15; add to cart adapted to Refresh's cart drawer. No dependencies. */
(() => {
  const formatMoney = (cents, format) => {
    const fmt = format || '${{amount}}';
    const key = (fmt.match(/\{\{\s*(\w+)\s*\}\}/) || [])[1] || 'amount';
    const delim = (n, d, t = ',', dec = '.') => {
      const [i, f] = (n / 100).toFixed(d).split('.');
      return i.replace(/\B(?=(\d{3})+(?!\d))/g, t) + (f ? dec + f : '');
    };
    const out = {
      amount: delim(cents, 2),
      amount_no_decimals: delim(cents, 0),
      amount_with_comma_separator: delim(cents, 2, '.', ','),
      amount_no_decimals_with_comma_separator: delim(cents, 0, '.'),
      amount_with_apostrophe_separator: delim(cents, 2, "'")
    }[key] ?? delim(cents, 2);
    return fmt.replace(/\{\{\s*\w+\s*\}\}/, out);
  };

  class SlGallery extends HTMLElement {
    connectedCallback() {
      this.slides = [...this.querySelectorAll('[data-slide]')];
      this.thumbs = [...this.querySelectorAll('[data-thumb]')];
      this.counter = this.querySelector('[data-count]');
      this.index = 0;
      this.thumbs.forEach((t, i) => t.addEventListener('click', () => this.go(i)));
      this.querySelector('[data-prev]')?.addEventListener('click', () => this.go(this.index - 1));
      this.querySelector('[data-next]')?.addEventListener('click', () => this.go(this.index + 1));
      this.addEventListener('keydown', e => {
        if (e.key === 'ArrowLeft') this.go(this.index - 1);
        if (e.key === 'ArrowRight') this.go(this.index + 1);
      });
      let x0 = null;
      const stage = this.querySelector('[data-stage]');
      stage?.addEventListener('touchstart', e => { x0 = e.touches[0].clientX; }, { passive: true });
      stage?.addEventListener('touchend', e => {
        if (x0 === null) return;
        const dx = e.changedTouches[0].clientX - x0;
        if (Math.abs(dx) > 40) this.go(this.index + (dx < 0 ? 1 : -1));
        x0 = null;
      });
    }
    go(i) {
      const n = this.slides.length;
      if (!n) return;
      const prev = this.index;
      this.index = (i + n) % n;
      if (prev !== this.index) {
        const leaving = this.slides[prev];
        leaving?.classList.add('is-leaving');
        clearTimeout(this._leaveTimer);
        this._leaveTimer = setTimeout(() => this.slides.forEach((s) => s.classList.remove('is-leaving')), 550);
      }
      this.slides.forEach((s, k) => s.classList.toggle('is-active', k === this.index));
      this.thumbs.forEach((t, k) => {
        t.classList.toggle('is-active', k === this.index);
        t.setAttribute('aria-current', k === this.index ? 'true' : 'false');
      });
      const t = this.thumbs[this.index];
      if (t) t.parentElement.scrollTo({ left: t.offsetLeft - t.parentElement.clientWidth / 2 + t.clientWidth / 2, behavior: 'smooth' });
      if (this.counter) this.counter.textContent = `${this.index + 1} / ${n}`;
    }
    goToMedia(id) {
      const i = this.slides.findIndex(s => s.dataset.mediaId === String(id));
      if (i > -1) this.go(i);
    }
  }
  customElements.get('sl-gallery') || customElements.define('sl-gallery', SlGallery);

  class SlBuy extends HTMLElement {
    connectedCallback() {
      this.product = JSON.parse(this.querySelector('[data-product-json]').textContent);
      this.moneyFormat = this.dataset.moneyFormat;
      this.form = this.querySelector('form');
      this.idInput = this.form.querySelector('input[name="id"]');
      this.qtyInput = this.form.querySelector('input[name="quantity"]');
      this.cta = this.querySelector('[data-cta]');
      this.ctaLabel = this.querySelector('[data-cta-label]');
      this.ctaPrice = this.querySelector('[data-cta-price]');
      this.ctaStrike = this.querySelector('[data-cta-strike]');
      this.error = this.querySelector('[data-error]');
      this.labels = { once: this.dataset.labelOnce, sub: this.dataset.labelSub, soldOut: this.dataset.labelSoldOut, added: this.dataset.labelAdded, preorder: this.dataset.labelPreorder };
      // Pre-order: set server-side when stock is below zero with a pre-order plan, or detected here once Restock Rocket
      // injects its pre-order plan into the form. One plan per cart line, so the subscription option is hidden then.
      this.preorder = this.dataset.preorder === 'true';
      new MutationObserver(() => this.detectPreorder()).observe(this.form, { childList: true, subtree: true });
      this.detectPreorder();

      this.form.addEventListener('change', () => this.update());
      this.querySelector('[data-qty-dec]')?.addEventListener('click', () => this.stepQty(-1));
      this.querySelector('[data-qty-inc]')?.addEventListener('click', () => this.stepQty(1));
      this.form.addEventListener('submit', e => this.submit(e));
      this.update();
    }
    detectPreorder() {
      if (this.preorder || !this.form.querySelector('input[name="selling_plan"].restock-rocket-form-input')) return;
      this.preorder = true;
      this.classList.add('is-preorder');
      const once = this.form.querySelector('input[name="sl-purchase"][value="once"]');
      if (once) once.checked = true;
      this.update();
    }
    stepQty(d) {
      const v = Math.max(1, (parseInt(this.qtyInput.value, 10) || 1) + d);
      this.qtyInput.value = v;
      this.update();
    }
    get variant() {
      const picked = this.form.querySelector('input[name="sl-variant"]:checked');
      const id = picked ? Number(picked.value) : Number(this.idInput.value);
      return this.product.variants.find(v => v.id === id) || this.product.variants[0];
    }
    update() {
      const v = this.variant;
      this.idInput.value = v.id;
      // bundle picker (1 / 2 / 3) sets the quantity
      const bundle = this.form.querySelector('input[name="sl-bundle"]:checked');
      if (bundle) this.qtyInput.value = bundle.value;
      const mode = this.preorder ? 'once' : this.form.querySelector('input[name="sl-purchase"]:checked')?.value || 'once';
      const sub = mode === 'sub';
      const planInputs = [...this.form.querySelectorAll('input[name="selling_plan"]')];
      planInputs.forEach(i => { i.disabled = !sub; });
      const planId = sub ? Number(planInputs.find(i => i.checked)?.value) : null;

      this.querySelectorAll('[data-plan]').forEach(el => el.classList.toggle('is-selected', el.dataset.plan === mode));

      // per-option prices
      this.querySelectorAll('[data-price-once]').forEach(el => { el.textContent = formatMoney(v.price, this.moneyFormat); });
      const firstPlan = planId || Number((planInputs.find((i) => i.checked) || planInputs[0])?.value);
      const subAlloc = v.selling_plan_allocations?.find(a => a.selling_plan_id === firstPlan);
      this.querySelectorAll('[data-price-sub]').forEach(el => { if (subAlloc) el.textContent = formatMoney(subAlloc.price, this.moneyFormat); });
      this.querySelectorAll('[data-price-sub-compare]').forEach(el => {
        if (subAlloc) el.textContent = formatMoney(subAlloc.compare_at_price || v.price, this.moneyFormat);
        el.hidden = !subAlloc || (subAlloc.compare_at_price || v.price) <= subAlloc.price;
      });
      // CTA
      const qty = Math.max(1, parseInt(this.qtyInput.value, 10) || 1);
      const alloc = planId ? v.selling_plan_allocations?.find(a => a.selling_plan_id === planId) : null;
      const unit = alloc ? alloc.price : v.price;
      const compare = alloc ? (alloc.compare_at_price || v.price) : (v.compare_at_price || 0);
      if (!this._added) this.ctaLabel.textContent = !v.available ? this.labels.soldOut : this.preorder ? this.labels.preorder : sub ? this.labels.sub : this.labels.once;
      this.cta.disabled = !v.available;
      if (this.ctaPrice) this.ctaPrice.textContent = formatMoney(unit * qty, this.moneyFormat);
      if (this.ctaStrike) {
        this.ctaStrike.hidden = !(compare > unit);
        this.ctaStrike.textContent = formatMoney(compare * qty, this.moneyFormat);
      }
      this.querySelectorAll('[data-cta-price-wrap]').forEach(el => { el.hidden = !v.available; });
      this.querySelectorAll('[data-bundle]').forEach(el => {
        const n = Number(el.dataset.bundle);
        el.classList.toggle('is-selected', el.querySelector('input').checked);
        el.querySelector('[data-bundle-total]').textContent = formatMoney(unit * n, this.moneyFormat);
        const strike = el.querySelector('[data-bundle-compare]');
        strike.hidden = !(compare > unit);
        strike.textContent = formatMoney(compare * n, this.moneyFormat);
        const each = el.querySelector('[data-bundle-each]');
        if (each) each.textContent = formatMoney(unit, this.moneyFormat);
      });

      // variant URL + media
      if (this._lastVariant !== v.id) {
        if (this._lastVariant !== undefined) {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', v.id);
          window.history.replaceState({}, '', url);
          if (v.featured_media) document.querySelector(`sl-gallery[data-section="${this.dataset.section}"]`)?.goToMedia(v.featured_media.id);
        }
        this._lastVariant = v.id;
      }
    }
    // Mirrors Refresh's product-form.js: ask the cart drawer (or notification) which sections it needs,
    // let it re-render and open itself, and publish the theme's cartUpdate event for other listeners.
    async submit(e) {
      if (this.dataset.ajax !== 'true') return;
      e.preventDefault();
      if (this.cta.getAttribute('aria-busy') === 'true') return;
      this.error.textContent = '';
      this.cta.setAttribute('aria-busy', 'true');
      const root = window.Shopify?.routes?.root || '/';
      const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
      // URL-encoded on purpose: Appstle's storefront script wraps fetch and, for FormData bodies, overwrites
      // selling_plan with its own widget's value (blank when that widget isn't on the page). String bodies pass through.
      const body = new URLSearchParams(new FormData(this.form));
      if (cart && typeof cart.getSectionsToRender === 'function') {
        body.append('sections', cart.getSectionsToRender().map((s) => s.id).join(','));
        body.append('sections_url', window.location.pathname);
        cart.setActiveElement?.(document.activeElement);
      }
      try {
        const res = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest', 'Content-Type': 'application/x-www-form-urlencoded' },
          body: body.toString()
        });
        const data = await res.json();
        if (!res.ok || data.status) throw new Error(data.description || data.message || 'Could not add to bag.');
        document.dispatchEvent(new CustomEvent('sl:cart:add', { bubbles: true, detail: data }));
        if (this.dataset.afterAdd === 'cart' || !cart || typeof cart.renderContents !== 'function') {
          window.location.href = `${root}cart`;
          return;
        }
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'sl-pdp', productVariantId: body.get('id'), cartData: data });
        }
        cart.classList.remove('is-empty');
        cart.renderContents(data);
        this.flashAdded();
      } catch (err) {
        this.error.textContent = err.message;
      } finally {
        this.cta.removeAttribute('aria-busy');
      }
    }
    flashAdded() {
      this._added = true;
      this.ctaLabel.textContent = this.labels.added;
      clearTimeout(this._t);
      this._t = setTimeout(() => { this._added = false; this.update(); }, 1800);
    }
  }
  customElements.get('sl-buy') || customElements.define('sl-buy', SlBuy);
})();
